import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import { sessionService, participantService } from '../../services';
import { useSession } from '../../contexts/SessionContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import tokenStorage from '../../utils/tokenStorage';

// Detect iOS Safari (not Chrome/Firefox/etc on iOS)
const isIOSSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIOS && isSafari;
};

// Privacy help shown to iOS Safari users when a join error occurs
const IOSSafariHelp = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      alert('Link copied! Now open Chrome and paste it in the address bar.');
    } catch {
      alert(currentUrl);
    }
  };

  return (
    <div className="mt-3 bg-amber-50 border border-amber-300 rounded-lg p-4 text-sm">
      <div className="flex items-start gap-2 mb-3">
        <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p className="font-semibold text-amber-900">iPhone Safari is blocking this join</p>
          <p className="text-xs text-amber-700 mt-0.5">Safari's privacy settings are preventing the request. Follow the steps below.</p>
        </div>
      </div>

      {/* Fastest fix: open in Chrome */}
      <div className="bg-white border border-amber-200 rounded-lg p-3 mb-3">
        <p className="font-semibold text-amber-900 mb-1">✅ Fastest fix — use Chrome</p>
        <p className="text-amber-800 mb-2">If Chrome is installed on your iPhone, copy this link and paste it into Chrome:</p>
        <button
          type="button"
          onClick={copyLink}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2 px-4 rounded-lg"
        >
          📋 Copy link &amp; open in Chrome
        </button>
      </div>

      {/* Manual instructions */}
      <p className="font-semibold text-amber-900 mb-1">Or reduce Safari privacy for this page:</p>
      <ol className="list-decimal list-inside space-y-2 text-amber-800 mb-3">
        <li>
          <b>Per-site (quickest):</b> tap the <b>aA</b> button in the address bar (top-left) →
          <b> Website Settings</b> → turn <b>OFF</b> "Use Content Blockers" and <b>OFF</b> "Hide IP Address" → tap <b>Done</b> → reload.
        </li>
        <li>
          <b>Global Safari settings:</b> open the iPhone <b>Settings</b> app → scroll to <b>Safari</b> → under <i>Privacy &amp; Security</i> turn <b>OFF</b>:
          <ul className="list-disc list-inside ml-4 mt-1">
            <li>Block All Cookies</li>
            <li>Prevent Cross-Site Tracking</li>
            <li>Hide IP Address (set to "Off")</li>
          </ul>
          Then return to Safari and reload this page.
        </li>
        <li>
          <b>Lockdown Mode:</b> Settings app → <b>Privacy &amp; Security</b> → <b>Lockdown Mode</b> → turn <b>OFF</b> (iPhone will restart).
        </li>
        <li>
          <b>Close Private Browsing:</b> tap the tabs icon (bottom-right) → switch from <b>"Private"</b> to <b>"Tabs"</b>.
        </li>
        <li>Reload this page and tap <b>Join Session</b> again.</li>
      </ol>

      <p className="text-xs text-amber-700">These settings only affect your own browser — your data stays safe.</p>
    </div>
  );
};

/**
 * Participant Join Page
 * Clean, minimal interface for joining a training session
 * Follows Slido-inspired design principles
 */

const ParticipantJoinPage = () => {
  const navigate = useNavigate();
  const { sessionCode: urlSessionCode } = useParams();
  const { joinSession } = useSession();
  const { formatTime } = usePreferences();
  const [sessionCode, setSessionCode] = useState(urlSessionCode || '');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
  });
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(urlSessionCode ? 'form' : 'code'); // 'code', 'form', or 'rejoin'

  // Multi-day session states
  const [showCodePopup, setShowCodePopup] = useState(false);
  const [participantCode, setParticipantCode] = useState('');
  const [pendingNavigate, setPendingNavigate] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);

  // Rejoin form state
  const [rejoinParticipantCode, setRejoinParticipantCode] = useState('');
  // Track whether we've already prefilled the rejoin code from storage for
  // this session — once true, we never overwrite the field again (otherwise
  // clearing the input would silently restore the saved code).
  const [hasPrefilledRejoinCode, setHasPrefilledRejoinCode] = useState(false);

  // Join form participant code (for multi-day sessions - optional)
  const [joinParticipantCode, setJoinParticipantCode] = useState('');

  // Returning participant (cookie-based recovery)
  const [returningParticipant, setReturningParticipant] = useState(null);

  // Pre-fill form with logged in user data if available
  useEffect(() => {
    const userData = tokenStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setFormData((prev) => ({
        ...prev,
        name: user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : prev.name,
        email: user.email || prev.email,
      }));
    }
  }, []);

  // Try to recover session from cookie (Safari/iOS fallback)
  useEffect(() => {
    const tryRecoverFromCookie = async () => {
      // Only attempt recovery if no participant data in storage
      const existingData = tokenStorage.getItem('current_participant') || tokenStorage.getItem('participantData');
      if (existingData) return;

      try {
        const response = await participantService.recoverSession();
        if (response.success && response.data) {
          const recovered = response.data;
          // Restore participant data to storage
          const participantData = {
            participantId: recovered.participantId,
            sessionId: recovered.sessionId,
            sessionCode: recovered.sessionCode,
            name: recovered.participantName,
            email: recovered.email || '',
            organization: recovered.organization || '',
            participantCode: recovered.participantCode,
            isMultiDay: recovered.isMultiDay,
          };
          tokenStorage.setItem('current_participant', JSON.stringify(participantData));
          tokenStorage.setItem(`participant_${recovered.sessionId}`, JSON.stringify(participantData));
          // Only present when we already held a token for this participant (refresh).
          tokenStorage.saveParticipantTokenFrom(recovered);

          // Navigate to the appropriate page based on session status
          if (recovered.status === 'live' || recovered.status === 'paused') {
            navigate(`/session/${recovered.sessionId}/active`, { replace: true });
          } else if (recovered.status === 'completed') {
            navigate(`/session/${recovered.sessionId}/reports`, { replace: true });
          } else {
            navigate(`/session/${recovered.sessionId}/lobby`, { replace: true });
          }
          return;
        }
      } catch {
        // No cookie or recovery failed — continue with normal join flow
      }
    };
    tryRecoverFromCookie();
  }, [navigate]);

  // If session code in URL, automatically look up session
  useEffect(() => {
    if (urlSessionCode) {
      findSession(urlSessionCode);
    }
  }, [urlSessionCode]);

  // Prefill the rejoin code from storage exactly once when we land on the
  // "Session Completed" branch — so the user can clear the field without it
  // immediately bouncing back to the saved code.
  useEffect(() => {
    if (hasPrefilledRejoinCode) return;
    if (!session || session.status !== 'completed') return;
    const existingParticipant = tokenStorage.getParticipantData(session.id);
    const savedCode = existingParticipant?.participantCode || '';
    if (savedCode) {
      setRejoinParticipantCode(savedCode);
    }
    setHasPrefilledRejoinCode(true);
  }, [session, hasPrefilledRejoinCode]);

  const findSession = async (code) => {
    setLoading(true);
    try {
      // Use API to find session by code
      const response = await sessionService.getSessionByCode(code);

      if (response.success && response.data) {
        const sessionData = response.data;
        const foundSession = {
          id: sessionData._id || sessionData.id,
          name: sessionData.name,
          code: sessionData.code,
          status: sessionData.status,
          trainerName: sessionData.trainerName ||
            (sessionData.trainer ? `${sessionData.trainer.firstName} ${sessionData.trainer.lastName}` : 'Trainer'),
          organizationName: sessionData.organizationName || sessionData.organization?.name || 'Training Platform',
          scheduledStartTime: sessionData.startTime || sessionData.scheduledDate,
          endTime: sessionData.endTime,
          maxParticipants: sessionData.maxParticipants,
          currentParticipants: sessionData.currentParticipants,
        };
        setSession(foundSession);
        setErrors({});

        // Check if this is a returning participant (via cookie)
        try {
          const checkResponse = await participantService.checkReturningParticipant(code);
          if (checkResponse.success && checkResponse.returning) {
            setReturningParticipant(checkResponse.participant);
            setStep('welcome_back');
            return;
          }
        } catch {
          // Cookie check failed — continue with normal join form
        }

        setStep('form');
      } else {
        setErrors({ sessionCode: response.error || 'Invalid session code. Please check and try again.' });
        setSession(null);
      }
    } catch (error) {
      console.error('Error finding session:', error);
      const errorMessage = error.response?.data?.error || 'Failed to find session. Please try again.';
      setErrors({ sessionCode: errorMessage });
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (!sessionCode) {
      setErrors({ sessionCode: 'Please enter a session code' });
      return;
    }
    findSession(sessionCode);
  };

  // Continue as returning participant (cookie recovery)
  const handleContinueAsReturning = async () => {
    if (!returningParticipant || !session) return;
    setLoading(true);
    try {
      // Rejoin using the existing participant data
      const participantData = {
        participantId: returningParticipant.id,
        sessionId: session.id,
        sessionCode: session.code,
        name: returningParticipant.name,
        email: returningParticipant.email || '',
        organization: returningParticipant.organization || '',
        participantCode: returningParticipant.participantCode,
        isMultiDay: !!returningParticipant.participantCode,
      };
      tokenStorage.setItem('current_participant', JSON.stringify(participantData));
      tokenStorage.setItem(`participant_${session.id}`, JSON.stringify(participantData));

      if (session.status === 'live' || session.status === 'paused') {
        navigate(`/session/${session.id}/active`, { replace: true });
      } else {
        navigate(`/session/${session.id}/lobby`, { replace: true });
      }
    } catch (error) {
      console.error('Error continuing as returning participant:', error);
      setErrors({ general: 'Failed to rejoin. Please try joining as a new participant.' });
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  // Join as someone else (ignore cookie, show fresh form). Without the
  // storage wipe, the next person to fill the form lands on the active
  // session page and sees the previous participant's "already responded"
  // flags + saved drafts/answers, all of which are session-scoped (not
  // participantId-scoped) in localStorage/sessionStorage.
  const handleJoinAsNewParticipant = () => {
    const sessionIdToClear = session?._id || session?.id;
    if (sessionIdToClear) {
      tokenStorage.clearSessionData(sessionIdToClear);
    }
    setReturningParticipant(null);
    setFormData({ name: '', email: '', organization: '' });
    setStep('form');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleJoinSession = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      let response;

      // Retry once on network-level failures (iOS Safari after screen-lock often
      // drops the first fetch attempt). HTTP 4xx/5xx responses are NOT retried —
      // those come back as {success:false, error:...} and we surface them as-is.
      const withNetworkRetry = async (fn) => {
        try {
          return await fn();
        } catch (err) {
          const msg = (err?.message || '').toLowerCase();
          const looksLikeNetworkError =
            err?.name === 'TypeError' ||
            msg.includes('failed to fetch') ||
            msg.includes('load failed') ||
            msg.includes('networkerror') ||
            msg.includes('network request failed');
          if (!looksLikeNetworkError) throw err;
          console.warn('[Join] network error, retrying once in 800ms:', err);
          await new Promise((r) => setTimeout(r, 800));
          return await fn();
        }
      };

      // If a participant code was supplied, treat this as a rejoin — recover
      // the existing record so responses and engagement carry over.
      if (joinParticipantCode.trim()) {
        response = await withNetworkRetry(() =>
          participantService.rejoinByCode({
            sessionCode: session.code,
            participantCode: joinParticipantCode.trim(),
          })
        );
      } else {
        // Check if existing participant data exists for THIS specific session (dual storage)
        const existingParticipant = tokenStorage.getParticipantData(session.id);

        // Use the public join endpoint (no auth required)
        // Pass existing participant ID if available for this session
        response = await withNetworkRetry(() =>
          participantService.joinSessionPublic({
            code: session.code,
            name: formData.name,
            email: formData.email || undefined,
            organization: formData.organization || undefined,
            existingParticipantId: existingParticipant?.participantId || undefined,
          })
        );
      }

      if (response.success) {
        console.log('Joined session:', response.data);

        // Create participant data with all info for localStorage
        const participantData = {
          participantId: response.data.participantId, // Unique ID from backend
          sessionId: response.data.sessionId,
          sessionCode: response.data.sessionCode,
          sessionName: response.data.sessionName,
          name: response.data.participantName || formData.name,
          email: formData.email || '',
          organization: formData.organization || '',
          joinedAt: Date.now(),
          participantCode: response.data.participantCode || null,
          isMultiDay: response.data.isMultiDay || false,
        };

        // Store participant info in dual storage (localStorage + sessionStorage)
        // This ensures Safari can't silently purge the data mid-session
        tokenStorage.setParticipantData(response.data.sessionId, participantData);
        // Signed identity: issued for a brand-new participant, or refreshed for a
        // returning one who still holds their token.
        tokenStorage.saveParticipantTokenFrom(response.data);

        // Join session via context (broadcasts to other tabs)
        joinSession(session.id, participantData);

        // Determine navigation target.
        // Phase 2: SessionLobbyPage now embeds PreSessionTab / PostSessionTab and
        // shows the welcome view with a sidebar — so /lobby is the right
        // destination for both scheduled-with-pre-session and post-session cases.
        // preSessionActive / postSessionActive flags are still passed in the
        // response so the lobby can default to the right sidebar tab.
        let navigateTo;
        if (response.data.reportOnly && response.data.status === 'completed') {
          navigateTo = `/session/${session.id}/reports`;
        } else {
          navigateTo = `/session/${session.id}/lobby`;
        }

        // Show the save-code popup only on FIRST join — when the user is
        // rejoining via a participant code they already typed in, they
        // obviously already have it. The backend reuses the same code on
        // rejoin (one participant = one code per session, forever) so the
        // popup would just be confusing noise. Same logic for the cookie/
        // localStorage-recovered "returning participant" branch handled
        // upstream.
        const isRejoinByCode = !!joinParticipantCode.trim();
        if (response.data.participantCode && !isRejoinByCode) {
          setParticipantCode(response.data.participantCode);
          setPendingNavigate(navigateTo);
          setShowCodePopup(true);
        } else {
          // Navigate directly — either no code yet, or this is a rejoin and
          // the participant already knows their code.
          navigate(navigateTo);
        }
      } else {
        setErrors({ submit: response.error || 'Failed to join session. Please try again.' });
      }
    } catch (error) {
      console.error('Error joining session:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.data?.error ||
        error.message ||
        'Failed to join session. Please check your internet and try again.';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // Handle rejoin with participant code (for multi-day sessions)
  const handleRejoinByCode = async (e) => {
    e.preventDefault();

    if (!sessionCode) {
      setErrors({ sessionCode: 'Please enter a session code' });
      return;
    }

    if (!rejoinParticipantCode) {
      setErrors({ participantCode: 'Please enter your participant code' });
      return;
    }

    setLoading(true);
    try {
      const response = await participantService.rejoinByCode({
        sessionCode: sessionCode,
        participantCode: rejoinParticipantCode,
      });

      if (response.success) {
        // Create participant data with all info for localStorage
        const sessionParticipantKey = `participant_${response.data.sessionId}`;
        const participantData = {
          participantId: response.data.participantId,
          sessionId: response.data.sessionId,
          sessionCode: response.data.sessionCode,
          sessionName: response.data.sessionName,
          name: response.data.participantName,
          participantCode: response.data.participantCode,
          isMultiDay: response.data.isMultiDay || true,
          joinedAt: Date.now(),
        };

        // Store participant info in dual storage
        tokenStorage.setParticipantData(response.data.sessionId, participantData);
        // Rejoining with the participant code proves identity, so a token is issued.
        tokenStorage.saveParticipantTokenFrom(response.data);

        // Join session via context
        joinSession(response.data.sessionId, participantData);

        // Navigate — always go to the lobby. The lobby surfaces post-session
        // activities (reflections / action plans) for completed sessions and
        // already exposes the participant's own report tab, so there's no
        // benefit to dead-ending on /reports.
        navigate(`/session/${response.data.sessionId}/lobby`);
      } else {
        setErrors({ submit: response.error || 'Failed to rejoin session. Please check your codes.' });
      }
    } catch (error) {
      console.error('Error rejoining session:', error);
      const errorMessage = error.response?.data?.error || 'Failed to rejoin session. Please check your codes.';
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // Handle closing the save code popup and navigating
  const handleCodePopupClose = () => {
    setShowCodePopup(false);
    if (pendingNavigate) {
      navigate(pendingNavigate);
    }
  };

  // Copy participant code to clipboard
  const copyCodeToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(participantCode);
      setCodeCopied(true);
      // Reset after 2 seconds
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Header - Clean and Simple */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-600 to-purple-600 rounded-xl mb-4">
            <span className="text-3xl font-bold text-white">#</span>
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Join Session</h2>
          <p className="text-sm text-neutral-600">Enter the code to participate</p>
        </div>

        {/* Step 1: Enter Session Code */}
        {step === 'code' && (
          <Card padding="lg" className="border border-neutral-200">
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Session Code
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xl font-bold text-primary-600 pointer-events-none">
                    #
                  </div>
                  <input
                    type="text"
                    value={sessionCode}
                    onChange={(e) => {
                      setSessionCode(e.target.value.toUpperCase());
                      setErrors({});
                    }}
                    placeholder="ABC123"
                    maxLength={6}
                    autoFocus
                    className="w-full pl-10 pr-4 py-3 text-xl font-bold text-center tracking-wider text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                  />
                </div>
                {errors.sessionCode && (
                  <p className="text-sm text-red-600 mt-2">{errors.sessionCode}</p>
                )}
              </div>

              <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
                {loading ? 'Finding Session...' : 'Find Session'}
              </Button>

              {/* Help text */}
              <p className="text-xs text-neutral-500 text-center">
                Enter the 6-character code provided by your trainer
              </p>

              {/* Rejoin option for multi-day sessions */}
              <div className="pt-4 border-t border-neutral-200">
                <button
                  type="button"
                  onClick={() => setStep('rejoin')}
                  className="w-full text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  Have a participant code? Rejoin here
                </button>
              </div>
            </form>
          </Card>
        )}

        {/* Step: Rejoin with Participant Code (for multi-day sessions) */}
        {step === 'rejoin' && (
          <Card padding="lg" className="border border-neutral-200">
            <form onSubmit={handleRejoinByCode} className="space-y-4">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-neutral-900">Rejoin Multi-Day Session</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Use your participant code to continue from where you left off
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Session Code
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xl font-bold text-primary-600 pointer-events-none">
                    #
                  </div>
                  <input
                    type="text"
                    value={sessionCode}
                    onChange={(e) => {
                      setSessionCode(e.target.value.toUpperCase());
                      setErrors({});
                    }}
                    placeholder="ABC123"
                    maxLength={6}
                    className="w-full pl-10 pr-4 py-3 text-xl font-bold text-center tracking-wider text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                  />
                </div>
                {errors.sessionCode && (
                  <p className="text-sm text-red-600 mt-2">{errors.sessionCode}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Participant Code
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold text-purple-600 pointer-events-none">
                    P-
                  </div>
                  <input
                    type="text"
                    value={rejoinParticipantCode}
                    onChange={(e) => {
                      setRejoinParticipantCode(e.target.value.toUpperCase());
                      setErrors({});
                    }}
                    placeholder="XXXX"
                    maxLength={6}
                    className="w-full pl-10 pr-4 py-3 text-xl font-bold text-center tracking-wider text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 uppercase"
                  />
                </div>
                {errors.participantCode && (
                  <p className="text-sm text-red-600 mt-2">{errors.participantCode}</p>
                )}
                <p className="text-xs text-neutral-500 mt-1">
                  The code you saved when you first joined (e.g., P-AB12)
                </p>
              </div>

              {errors.submit && (
                <div>
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {errors.submit}
                  </div>
                  {isIOSSafari() && <IOSSafariHelp />}
                </div>
              )}

              <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
                {loading ? 'Rejoining...' : 'Rejoin Session'}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('code');
                  setErrors({});
                  setRejoinParticipantCode('');
                }}
                className="w-full text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                Join as new participant
              </button>
            </form>
          </Card>
        )}

        {/* Step: Welcome Back (returning participant via cookie) */}
        {step === 'welcome_back' && session && returningParticipant && (
          <Card padding="lg" className="border border-neutral-200">
            <div className="text-center py-4">
              {/* Welcome icon */}
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h3 className="text-lg font-semibold text-neutral-900 mb-1">Welcome back!</h3>
              <p className="text-sm text-neutral-600 mb-6">
                You were previously in this session
              </p>

              {/* Participant info card */}
              <div className="bg-neutral-50 rounded-xl p-4 mb-6 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-lg">
                    {returningParticipant.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900">{returningParticipant.name}</p>
                    {returningParticipant.email && (
                      <p className="text-sm text-neutral-500">{returningParticipant.email}</p>
                    )}
                    {returningParticipant.organization && (
                      <p className="text-xs text-neutral-400">{returningParticipant.organization}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Session info */}
              <div className="bg-blue-50 rounded-lg p-3 mb-6 text-sm text-blue-800">
                <p className="font-medium">{session.name}</p>
                <p className="text-blue-600 text-xs mt-1">Hosted by {session.organizationName}</p>
              </div>

              {/* Actions */}
              <button
                onClick={handleContinueAsReturning}
                disabled={loading}
                className="w-full py-3 px-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-all mb-3"
              >
                {loading ? 'Rejoining...' : `Continue as ${returningParticipant.name}`}
              </button>

              <button
                onClick={handleJoinAsNewParticipant}
                className="w-full py-2 px-4 text-neutral-600 hover:text-neutral-900 font-medium text-sm transition-all"
              >
                Join as someone else
              </button>
            </div>
          </Card>
        )}

        {/* Step 2: Join Session Form */}
        {step === 'form' && session && (
          <Card padding="lg" className="border border-neutral-200">
            {/* Session Status Check */}
            {['expired', 'cancelled'].includes(session.status) ? (
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2">Session Unavailable</h3>
                <p className="text-neutral-600 mb-4">
                  {session.status === 'expired' && 'This session has expired.'}
                  {session.status === 'cancelled' && 'This session has been cancelled.'}
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep('code');
                    setSession(null);
                    setSessionCode('');
                  }}
                >
                  Try Another Code
                </Button>
              </div>
            ) : session.status === 'completed' ? (
              // Completed session — Phase 2 keeps the lobby reachable so
              // attendees can still pick up their post-session activities
              // (reflections, action plans, etc.). The one-time prefill of
              // rejoinParticipantCode from storage happens in a useEffect
              // above so clearing the field doesn't trigger a re-prefill.
              (
                  <form onSubmit={handleRejoinByCode} className="space-y-5">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                        <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-neutral-900 mb-2">Session Completed</h3>
                      <p className="text-sm text-neutral-600 leading-relaxed">
                        This session has ended. Enter your participant code to access post-session activities.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Participant Code
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold text-purple-600 pointer-events-none">
                          P-
                        </div>
                        <input
                          type="text"
                          value={rejoinParticipantCode}
                          onChange={(e) => {
                            setRejoinParticipantCode(e.target.value.toUpperCase());
                            setErrors({});
                          }}
                          placeholder="XXXXX"
                          maxLength={6}
                          autoFocus
                          className="w-full pl-10 pr-4 py-3 text-xl font-bold text-center tracking-wider text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 uppercase"
                        />
                      </div>
                      {errors.participantCode && (
                        <p className="text-sm text-red-600 mt-2">{errors.participantCode}</p>
                      )}
                      <p className="text-xs text-neutral-500 mt-1">
                        The code you saved when you first joined this session.
                      </p>
                    </div>

                    {errors.submit && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                        {errors.submit}
                      </div>
                    )}

                    <Button type="submit" variant="primary" fullWidth disabled={loading}>
                      {loading ? 'Joining…' : 'Continue to Session'}
                    </Button>

                    <button
                      type="button"
                      onClick={() => {
                        setStep('code');
                        setSession(null);
                        setSessionCode('');
                        setRejoinParticipantCode('');
                        setErrors({});
                      }}
                      className="w-full text-sm text-neutral-600 hover:text-neutral-900 font-medium transition-colors"
                    >
                      Try Another Code
                    </button>
                    </form>
              )
            ) : (
              <>
            {/* Session Info */}
            <div className="text-center mb-6 pb-6 border-b border-neutral-200">
              <h3 className="text-xl font-bold text-neutral-900 mb-2">{session.name}</h3>
              <p className="text-sm text-neutral-600">
                Trainer: {session.trainerName} | {session.organizationName || 'Training Platform'}
              </p>
              {session.scheduledStartTime && (
                <p className="text-sm text-neutral-500 mt-1">
                  {new Date(session.scheduledStartTime).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })} at {formatTime(session.scheduledStartTime)}
                </p>
              )}
              {session.status === 'live' && (
                <div className="inline-flex items-center gap-2 mt-2 px-3 py-1 bg-green-100 rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-green-700">Live Now</span>
                </div>
              )}
            </div>

            {/* Join Form */}
            <form onSubmit={handleJoinSession} className="space-y-4">
              <Input
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={errors.name}
                placeholder="John Doe"
                required
                fullWidth
                helperText="*Required"
              />

              <Input
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="john@company.com"
                fullWidth
                helperText="Optional - for reports"
              />

              <Input
                label="Organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                placeholder="ABC Corporation"
                fullWidth
                helperText="Optional"
              />

              {/* Participant Code — always shown. Phase 2: every participant gets a
                  code for re-entry (pre-session tasks, post-session tracking, multi-day). */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Participant Code
                </label>
                <input
                  type="text"
                  value={joinParticipantCode}
                  onChange={(e) => {
                    setJoinParticipantCode(e.target.value.toUpperCase());
                    if (errors.joinParticipantCode) {
                      setErrors((prev) => ({ ...prev, joinParticipantCode: '' }));
                    }
                  }}
                  placeholder="E.G. PAB3K"
                  maxLength={6}
                  className="w-full px-3 py-2 text-sm font-medium tracking-wider text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                />
                {errors.joinParticipantCode && (
                  <p className="text-sm text-red-600 mt-1">{errors.joinParticipantCode}</p>
                )}
                <p className="text-xs text-neutral-500 mt-1">
                  If you are joining for the first time, skip this field. If you have joined before, enter your participant code to continue with your previous progress.
                </p>
              </div>

              {errors.submit && (
                <div>
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {errors.submit}
                  </div>
                  {isIOSSafari() && <IOSSafariHelp />}
                </div>
              )}

              <Button type="submit" variant="primary" size="lg" fullWidth loading={loading}>
                {loading ? 'Joining...' : 'Join Session'}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('code');
                  setSession(null);
                  setSessionCode('');
                }}
                className="w-full text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                Change Session
              </button>
            </form>
              </>
            )}
          </Card>
        )}
      </div>

      {/* Save Your Code popup — shown to every participant on first join.
          Recovers identity if they get disconnected, switch devices, lose
          localStorage (Safari ITP), or rejoin on another day. */}
      <AnimatePresence>
        {showCodePopup && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={handleCodePopupClose}
            />

            {/* Popup Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="relative z-10 w-full max-w-sm mx-4"
            >
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Save Your Code!</h3>
                      <p className="text-purple-100 text-sm">Use it to rejoin if you get disconnected</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-sm text-neutral-600 mb-4">
                    Save this code so you can rejoin if you get disconnected, switch devices, or come back later — your responses and engagement will be preserved.
                  </p>

                  {/* Code Display */}
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-4">
                    <p className="text-xs text-purple-600 font-medium mb-1">Your Participant Code</p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-purple-700 tracking-wider">
                        {participantCode}
                      </span>
                      <button
                        onClick={copyCodeToClipboard}
                        className={`p-2 rounded-lg transition-colors flex items-center gap-1 ${
                          codeCopied
                            ? 'bg-green-100 text-green-600'
                            : 'text-purple-600 hover:bg-purple-100'
                        }`}
                        title={codeCopied ? 'Copied!' : 'Copy to clipboard'}
                      >
                        {codeCopied ? (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-xs font-medium">Copied</span>
                          </>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-xs font-medium text-amber-800">Keep this code safe!</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          Take a screenshot or note it down. You'll need it if you get disconnected and your browser data isn't preserved.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={handleCodePopupClose}
                  >
                    Got it, Continue
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ParticipantJoinPage;
